// src/pages/MovesPage.js (full updated)
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../scripts/api";
import CardComponent from "../components/CardComponent";
import Modal from "../components/Modal";
import NotesWindow from "../components/NotesWindow";

function toNumId(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// Human-friendly age label + hour count for a DB timestamp. Used by the
// Ready-at-Factory list to show "3d 4h" / "12h" / "45m" so the operator
// can triage which units have been waiting longest.
function ageFromTimestamp(ts) {
  if (!ts) return null;
  const then = new Date(String(ts).replace(" ", "T"));
  if (!Number.isFinite(then.getTime())) return null;
  const ms = Date.now() - then.getTime();
  if (ms < 0) return { label: "future", hours: 0 };
  const mins = Math.floor(ms / 60000);
  if (mins < 60)           return { label: `${mins}m`, hours: 0 };
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)            return { label: `${hrs}h`,  hours: hrs };
  const days = Math.floor(hrs / 24);
  const remH = hrs - days * 24;
  return { label: remH ? `${days}d ${remH}h` : `${days}d`, hours: hrs };
}

// Colour-code the age badge: green <24h, amber 1–3d, red >3d. Mirrors the
// rough "fresh → stale → urgent" triage buckets the operator cares about.
function ageBadge(hours) {
  if (hours == null)  return { color: "#94a3b8" };
  if (hours >= 72)    return { color: "#dc2626" };
  if (hours >= 24)    return { color: "#ea580c" };
  return                     { color: "#16a34a" };
}

// Turn the POST /moves (or PATCH /reassign) `notify` payload into a short
// human-readable tail for the success toast — lets the operator see at a
// glance whether the driver and ops actually got their emails.
function summarizeAssignmentNotify(n) {
  if (!n) return "";
  const bits = [];
  const fmt = (label, r) => {
    if (!r) return null;
    if (r.ok) {
      const sent = Array.isArray(r.results) ? r.results.filter((x) => x.ok).length : 0;
      return `${label} → ${sent || "sent"}`;
    }
    const reasonMap = {
      event_not_configured:   "event missing",
      event_disabled:         "event disabled",
      no_recipients:          "no recipients",
      mailgun_not_configured: "MailGun not configured",
      no_driver_email:        "driver has no email on file",
      dispatch_error:         `error: ${r.error || "unknown"}`,
    };
    return `${label} ⚠ ${reasonMap[r.reason] || r.reason || "failed"}`;
  };
  const da = fmt("ops", n.driver_assigned);
  const dm = fmt("driver", n.driver_move_assignment);
  if (da) bits.push(da);
  if (dm) bits.push(dm);
  return bits.length ? " · " + bits.join(" · ") : "";
}

export default function MovesPage() {
  const [moves, setMoves] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [drvById, setDrvById] = useState({});
  const [selectedMove, setSelectedMove] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  // inventory hydration cache: { [id]: record|null }
  const [invById, setInvById] = useState({});

  // Form fields for create/edit
  const [destination, setDestination] = useState("");
  const [airline, setAirline] = useState("");
  const [flightNum, setFlightNum] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [permits, setPermits] = useState("");
  const [driverId, setDriverId] = useState("");
  const [notes, setNotes] = useState("");
  const [inventoryId, setInventoryId] = useState("");
  const [rowo, setRowo] = useState("");
  const [chassisVin, setChassisVin] = useState("");
  const [moveType, setMoveType] = useState("factory_to_sna");
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState("");
  const [destinationId, setDestinationId] = useState("");
  const [destinations, setDestinations] = useState([]);

  // Search modal state
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [readyUnits, setReadyUnits] = useState([]);
  const [readyBusy,  setReadyBusy]  = useState(false);

  // Breakdown state
  // { [inventory_id]: openBreakdownRow | null }
  const [openBreakdownByUnit, setOpenBreakdownByUnit] = useState({});
  const [breakdownMove, setBreakdownMove] = useState(null);     // move currently being reported on
  const [resolveBreakdown, setResolveBreakdown] = useState(null); // breakdown currently being resolved

  // Attachments state — { [TripId]: [attachment, ...] } cached from the last list fetch
  const [attachmentsByMove, setAttachmentsByMove] = useState({});
  const [attachmentsMove,   setAttachmentsMove]   = useState(null); // move whose attachments modal is open

  // Breakdown notes modal (admin side)
  const [notesBreakdown, setNotesBreakdown] = useState(null); // breakdown row

  // Unit notes window (inventory-level notes, opened from the Notes button
  // on each inventory card embedded in a move row).
  const [notesOpenId, setNotesOpenId] = useState(null);
  const [notesTitle,  setNotesTitle]  = useState("");

  const openNotesForUnit = useCallback((rec) => {
    if (!rec?.id) return;
    const desc =
      rec.make_model ||
      rec.chassis_make_model ||
      rec.body_make_model ||
      rec.body_model_size ||
      rec.description ||
      rec.desc ||
      "";
    setNotesTitle(`${rec.customer || "Unit"} — ${desc || `#${rec.id}`}`);
    setNotesOpenId(rec.id);
  }, []);

  useEffect(() => {
    loadMoves();
    loadDrivers();
    loadOpenBreakdowns();
    loadDestinations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDestinations = async () => {
    try {
      const { data } = await api.get("/destinations", { params: { active: "1" } });
      setDestinations(Array.isArray(data?.destinations) ? data.destinations : []);
    } catch (_) { /* non-fatal; admin can still type destination */ }
  };

  const loadOpenBreakdowns = async () => {
    try {
      const { data } = await api.get("/breakdowns", { params: { status: "open" } });
      const byUnit = {};
      for (const b of (data?.breakdowns || [])) byUnit[b.inventory_id] = b;
      setOpenBreakdownByUnit(byUnit);
    } catch (e) {
      console.error("Failed to load breakdowns", e);
    }
  };

  const loadMoveAttachments = async (tripId) => {
    try {
      const { data } = await api.get(`/moves/${tripId}/attachments`);
      const list = Array.isArray(data) ? data : (data?.attachments || []);
      setAttachmentsByMove((prev) => ({ ...prev, [tripId]: list }));
      return list;
    } catch (e) {
      console.error("Failed to load attachments", e);
      return [];
    }
  };

  const hydrateInventoryForMoves = async (list) => {
    const ids = Array.from(
      new Set(
        (list || [])
          .map((m) => toNumId(m.inventory_id))
          .filter((n) => n != null)
      )
    );

    // fetch only missing ids
    const missing = ids.filter((id) => !(id in invById));
    if (!missing.length) return;

    // parallel fetch; store null on failure to avoid retry loops
    const fetched = await Promise.all(
      missing.map(async (id) => {
        try {
          const { data } = await api.get(`/all_inventory/${id}`);
          return [id, data || null];
        } catch {
          return [id, null];
        }
      })
    );

    setInvById((prev) => {
      const next = { ...prev };
      for (const [id, rec] of fetched) next[id] = rec;
      return next;
    });
  };

  const loadMoves = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.get("/moves");
      let list = Array.isArray(data) ? data : [];
      if (!showCompleted) list = list.filter((m) => !m.completed_at);
      // Sort: active first (null completed_at), then most-recently completed first.
      list.sort((a, b) => {
        if (!a.completed_at && b.completed_at) return -1;
        if (a.completed_at && !b.completed_at) return 1;
        if (a.completed_at && b.completed_at) return String(b.completed_at).localeCompare(String(a.completed_at));
        return Number(b.TripId) - Number(a.TripId);
      });
      setMoves(list);
      await hydrateInventoryForMoves(list);
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load moves.");
      setMoves([]);
    } finally {
      setBusy(false);
    }
  };

  // Reload when the toggle flips
  useEffect(() => { loadMoves(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [showCompleted]);

  const loadDrivers = async () => {
    try {
      const { data } = await api.get("/driver");
      const list = Array.isArray(data) ? data : [];
      setDrivers(list);
      const byId = {};
      for (const d of list) byId[d.DriverId] = d;
      setDrvById(byId);
    } catch (e) {
      console.error("Failed to load drivers", e);
    }
  };

  const createOrUpdateMove = async () => {
    if (!inventoryId) {
      setMsg("Inventory ID required.");
      return;
    }

    const payload = {
      Destination: destination,
      Airline: airline,
      FlightNum: flightNum,
      Confirmation: confirmation,
      Permits: permits,
      driver_id: toNumId(driverId) || null,
      Notes: notes,
      inventory_id: inventoryId,
      rowo: rowo,
      chassis_vin: chassisVin,
      move_type: moveType,
      start_date: startDate || null,
      end_date:   endDate   || null,
      destination_id: destinationId || null,
    };

    setBusy(true);
    setMsg("");
    try {
      let resp;
      if (editMode) {
        resp = await api.put(`/moves/${selectedMove.TripId}`, payload);
        setMsg("Move updated successfully." + summarizeAssignmentNotify(resp?.data?.notify));
      } else {
        resp = await api.post("/moves", payload);
        setMsg("Move created successfully." + summarizeAssignmentNotify(resp?.data?.notify));
      }

      // refresh list + ensure the selected inventory record is hydrated
      resetForm();
      await loadMoves();
      closeModal();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Operation failed.");
    } finally {
      setBusy(false);
    }
  };

  const deleteMove = async (id) => {
    if (!window.confirm("Delete this move?")) return;
    setBusy(true);
    try {
      await api.delete(`/moves/${id}`);
      setMsg("Move deleted.");
      await loadMoves();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  const completeMove = async (moveId, mt) => {
    const prompt = mt === "demo"
      ? "Mark this demo as returned? The unit's lifecycle state is unchanged — it returns to its prior state at SNA."
      : mt === "sna_to_customer"
        ? "Complete this move and mark the unit delivered to the customer?\n\n(PDI must be complete — otherwise this will fail.)"
        : "Complete this move and mark the unit arrived at SNA?";
    if (!window.confirm(prompt)) return;
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post(`/moves/${moveId}/complete`);
      const newState = data?.transition?.new_state;
      let note = newState ? `Move completed — unit state: ${newState}` : "Move completed.";
      const n = data?.notify;
      if (n) {
        if (n.ok) {
          const sent = Array.isArray(n.results) ? n.results.filter(r => r.ok).length : 0;
          note += sent ? ` · notification sent to ${sent} recipient(s).` : " · notification dispatched.";
        } else {
          const reasonMap = {
            event_not_configured:   "event not configured — check Admin → Notifications",
            event_disabled:         "event is disabled — enable it in Admin → Notifications",
            no_recipients:          "no recipients — add addresses in Admin → Notifications",
            mailgun_not_configured: "MailGun not configured on the server",
            dispatch_error:         `dispatch error: ${n.error || "unknown"}`,
          };
          note += ` · ⚠ notification NOT sent (${reasonMap[n.reason] || n.reason || "unknown"}).`;
        }
      }
      setMsg(note);
      await loadMoves();
    } catch (e) {
      const err = e?.response?.data;
      const full = err?.hint ? `${err.error}\n\n${err.hint}` : (err?.error || "Failed to complete move.");
      setMsg(full);
    } finally {
      setBusy(false);
    }
  };

  const endMove = async (moveId, invId, statusCode) => {
    if (!window.confirm(`Confirm: Set inventory status to ${statusCode} and end move?`)) return;
    setBusy(true);
    setMsg("");
    try {
      // Update inventory status
      await api.put(`/all_inventory/${invId}`, { color_code: statusCode });

      // Delete move
      await api.delete(`/moves/${moveId}`);

      setMsg("Move ended successfully.");
      await loadMoves();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to end move.");
    } finally {
      setBusy(false);
    }
  };

  const openModal = async (move = null) => {
    setEditMode(!!move);

    if (move) {
      setDestination(move.destination || "");
      setAirline(move.Airline || "");
      setFlightNum(move.FlightNum || "");
      setConfirmation(move.Confirmation || "");
      setPermits(move.Permits || "");
      setDriverId(move.driver_id || "");
      setNotes(move.Notes || "");
      setInventoryId(move.inventory_id || "");
      setRowo(move.rowo || "");
      setChassisVin(move.chassis_vin || "");
      setMoveType(move.move_type || "factory_to_sna");
      setStartDate(move.start_date ? String(move.start_date).slice(0, 10) : "");
      setEndDate(move.end_date ? String(move.end_date).slice(0, 10) : "");
      setDestinationId(move.destination_id || "");
    } else {
      resetForm();
    }

    setSelectedMove(move || {});

    // hydrate for edit preview too
    const invId = toNumId(move?.inventory_id);
    if (invId != null && !(invId in invById)) {
      try {
        const { data } = await api.get(`/all_inventory/${invId}`);
        setInvById((prev) => ({ ...prev, [invId]: data || null }));
      } catch {
        setInvById((prev) => ({ ...prev, [invId]: null }));
      }
    }
  };

  const closeModal = () => {
    setSelectedMove(null);
  };

  const resetForm = () => {
    setDestination("");
    setAirline("");
    setFlightNum("");
    setConfirmation("");
    setPermits("");
    setDriverId("");
    setNotes("");
    setInventoryId("");
    setRowo("");
    setChassisVin("");
    setMoveType("factory_to_sna");
    setStartDate(new Date().toISOString().slice(0, 10));
    setEndDate("");
    setDestinationId("");
  };

  // Search functions
const loadReadyUnits = async () => {
    setReadyBusy(true);
    try {
      const { data } = await api.get("/moves/ready-units", { params: { limit: 100 } });
      setReadyUnits(Array.isArray(data?.units) ? data.units : []);
    } catch (e) {
      console.error("Failed to load ready units", e);
      setReadyUnits([]);
    } finally {
      setReadyBusy(false);
    }
  };

  const openSearchModal = () => {
    setShowSearchModal(true);
    loadReadyUnits();
  };

  const searchInventory = async () => {
    if (!searchQuery.trim()) return;
    setSearchBusy(true);
    try {
      const term = searchQuery.trim().toLowerCase();
      const { data } = await api.get("all_inventory", { params: { q: searchQuery } });
      const list = Array.isArray(data) ? data : [];

      const filtered = list.filter((r) => {
        const rowo = String(r.rowo || "").toLowerCase();
        const factoryWo = String(r.factory_wo || "").toLowerCase();
        const stockPo = String(r.stock_po || "").toLowerCase();
        const chassisVin = String(r.chassis_vin || "").toLowerCase();
        const bodyVin = String(r.body_vin || "").toLowerCase();
        const prodOrder = String(r.factory_production_so_order || "").toLowerCase();
        return (
          (rowo && rowo.includes(term)) ||
          (factoryWo && factoryWo.includes(term)) ||
          (stockPo && stockPo.includes(term)) ||
          (chassisVin && chassisVin.includes(term)) ||
          (bodyVin && bodyVin.includes(term)) ||
          (prodOrder && prodOrder.includes(term))
        );
      });

      setSearchResults(filtered);
    } catch (e) {
      console.error("Search failed", e);
      setSearchResults([]);
    } finally {
      setSearchBusy(false);
    }
  };

  const selectInventory = async (record) => {
    const id = record?.id ?? "";
    setInventoryId(id);
    setRowo(record?.rowo || "");
    setChassisVin(record?.chassis_vin || "");
    setShowSearchModal(false);

    const invId = toNumId(id);
    if (invId != null) {
      // we already have the record from search results; cache it
      setInvById((prev) => ({ ...prev, [invId]: record || null }));
    }
  };

  const moveCards = useMemo(() => {
    return moves.map((m) => {
      const invId = toNumId(m.inventory_id);
      const invRec = invId != null ? invById[invId] : null;
      return { m, invId, invRec };
    });
  }, [moves, invById]);

  const selectedInvId = toNumId(selectedMove?.inventory_id);
  const selectedInvRec = selectedInvId != null ? invById[selectedInvId] : null;

  return (
    <div className="container">
      <div
        className="row"
        style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}
      >
        <h2 style={{ margin: 0, fontWeight: 900 }}>Drive-Away Operations</h2>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginLeft: "auto" }}>
          <input type="checkbox" checked={showCompleted} onChange={e => setShowCompleted(e.target.checked)} />
          Include completed
        </label>
        <button className="btn-outline" onClick={loadMoves} disabled={busy}>
          Refresh
        </button>
        <button className="btn" onClick={() => openModal()} disabled={busy}>
          Add A New Move
        </button>
      </div>

      {msg ? (
        <div className="muted" style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>
          {msg}
        </div>
      ) : null}

      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Moves In Progress</div>
        {busy ? <div className="muted">Loading…</div> : null}

        <div style={{ display: "grid", gap: 10 }}>
          {moveCards.map(({ m, invId, invRec }) => (
            <div key={m.TripId} className="card" style={{ background: m.completed_at ? "rgba(22,163,74,0.04)" : "rgba(255,255,255,0.02)", position: "relative", opacity: m.completed_at ? 0.82 : 1 }}>
              <div
                className="row"
                style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}
              >
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 900 }}>Move #{m.TripId}</div>
                    {(() => {
                      const t = m.move_type;
                      const style = t === "demo"
                        ? { bg: "#f3e8ff", fg: "#6b21a8", label: "DEMO (OUT & BACK)" }
                        : t === "sna_to_customer"
                          ? { bg: "#dcfce7", fg: "#166534", label: "SNA → CUSTOMER" }
                          : { bg: "#dbeafe", fg: "#1e40af", label: "FACTORY → SNA" };
                      return (
                        <span style={{
                          background: style.bg, color: style.fg,
                          borderRadius: 99, padding: "2px 10px",
                          fontSize: 11, fontWeight: 800, letterSpacing: 0.4,
                        }}>
                          {style.label}
                        </span>
                      );
                    })()}
                    {openBreakdownByUnit[toNumId(m.inventory_id)] && (
                      <span
                        title={`Case #${openBreakdownByUnit[toNumId(m.inventory_id)].casenumber || "—"} · ${openBreakdownByUnit[toNumId(m.inventory_id)].svcdealer || "no dealer"}`}
                        style={{
                          background: "#fee2e2", color: "#991b1b",
                          borderRadius: 99, padding: "2px 10px", fontSize: 11, fontWeight: 800, letterSpacing: 0.4,
                        }}
                      >
                        🚨 BROKEN DOWN
                      </span>
                    )}
                    {m.completed_at && (
                      <span
                        title={`Completed ${String(m.completed_at).slice(0, 16).replace('T', ' ')}`}
                        style={{
                          background: "#dcfce7", color: "#166534",
                          borderRadius: 99, padding: "2px 10px", fontSize: 11, fontWeight: 800, letterSpacing: 0.4,
                        }}
                      >
                        ✓ COMPLETED {String(m.completed_at).slice(0, 10)}
                      </span>
                    )}
                  </div>
                  <div className="muted" style={{ marginTop: 4 }}>
                    Inventory Id: {m.inventory_id || "(none)"} • Location: {m.destination || "(none)"} • Driver:{" "}
                    {drvById[m.driver_id]?.FullName || "(none)"}
                  </div>
                  {(m.start_date || m.end_date) && (() => {
                    const today = new Date().toISOString().slice(0, 10);
                    const sd = m.start_date ? String(m.start_date).slice(0, 10) : null;
                    const isFuture = sd && sd > today;
                    const isToday  = sd && sd === today;
                    const color = isFuture ? "#2563eb" : isToday ? "#16a34a" : "#6b7280";
                    return (
                      <div style={{ marginTop: 4, fontSize: 13, fontWeight: 700, color }}>
                        {isFuture && "📅 Scheduled for "}
                        {isToday  && "🚛 Scheduled today — "}
                        {!isFuture && !isToday && sd && "Started "}
                        {sd}
                        {m.end_date && ` → ${String(m.end_date).slice(0, 10)}`}
                      </div>
                    );
                  })()}
                </div>

                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <button className="btn-outline" onClick={() => openModal(m)} disabled={busy}
                    title={m.completed_at ? "Edit destination / notes (completed moves stay in history)" : "Edit"}>
                    Edit
                  </button>
                  <button
                    className="btn-outline"
                    onClick={async () => {
                      await loadMoveAttachments(m.TripId);
                      setAttachmentsMove(m);
                    }}
                    disabled={busy}
                    title="Upload / manage documents (airline tickets, permits, etc.)"
                  >
                    📎 Docs{attachmentsByMove[m.TripId]?.length ? ` (${attachmentsByMove[m.TripId].length})` : ""}
                  </button>
                  <button className="btn-outline" onClick={() => deleteMove(m.TripId)} disabled={busy}>
                    Delete
                  </button>
                  {!m.completed_at && (
                    openBreakdownByUnit[toNumId(m.inventory_id)] ? (
                      <>
                        <button
                          className="btn-outline"
                          style={{ color: "#991b1b", borderColor: "#fca5a5" }}
                          onClick={() => setNotesBreakdown(openBreakdownByUnit[toNumId(m.inventory_id)])}
                          disabled={busy}
                          title="View / add breakdown notes — shared with the driver"
                        >
                          💬 Notes
                        </button>
                        <button
                          className="btn"
                          style={{ background: "#059669", color: "#fff" }}
                          onClick={() => setResolveBreakdown(openBreakdownByUnit[toNumId(m.inventory_id)])}
                          disabled={busy}
                          title="Mark unit as repaired and ready for pickup from repair"
                        >
                          Mark Repaired
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn-outline"
                        style={{ color: "#dc2626", borderColor: "#dc2626" }}
                        onClick={() => setBreakdownMove(m)}
                        disabled={busy}
                        title="Report a breakdown for this unit"
                      >
                        🚨 Breakdown
                      </button>
                    )
                  )}
                  {!m.completed_at && (
                    <button className="btn" onClick={() => completeMove(m.TripId, m.move_type)} disabled={busy || openBreakdownByUnit[toNumId(m.inventory_id)]}>
                      {m.move_type === "demo"             ? "Mark Returned"
                       : m.move_type === "sna_to_customer" ? "Mark Delivered"
                       :                                     "Mark Arrived at SNA"}
                    </button>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 10 }}>
                {/* ✅ render full inventory record when available */}
                <CardComponent
                  record={invRec || (invId != null ? { id: invId } : {})}
                  table="all_inventory"
                  onOpenNotes={() => openNotesForUnit(invRec || { id: invId })}
                  onAddNote={() => openNotesForUnit(invRec || { id: invId })}
                />
                {invId != null && invRec === null ? (
                  <div className="muted" style={{ marginTop: 8 }}>
                    Couldn’t load inventory record #{invId}.
                  </div>
                ) : null}
              </div>

              {/* Hover Overlay */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background: "rgba(0,0,0,0.5)",
                  display: "none",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  flexDirection: "column",
                }}
                className="overlay"
                onMouseEnter={(e) => e.currentTarget.style.display = "flex"}
                onMouseLeave={(e) => e.currentTarget.style.display = "none"}
              >
                <button className="btn" onClick={() => endMove(m.TripId, m.inventory_id, 13)} disabled={busy}>
                  Broke Down
                </button>
                <button className="btn" onClick={() => endMove(m.TripId, m.inventory_id, 3)} disabled={busy}>
                  Complete Move
                </button>
              </div>
            </div>
          ))}

          {!busy && moves.length === 0 ? <div className="muted">No moves found.</div> : null}
        </div>
      </div>

      {selectedMove && (
        <Modal onClose={closeModal}>
          <h2>{editMode ? "Edit Move" : "Create Move"}</h2>

          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            <select
              className="input"
              value={moveType}
              onChange={(e) => setMoveType(e.target.value)}
              title="Determines what happens on Complete Move"
            >
              <option value="factory_to_sna">Factory → SNA (pickup + transport in)</option>
              <option value="sna_to_customer">SNA → Customer (final delivery, requires PDI complete)</option>
              <option value="demo">Demo (out-and-back from SNA, no lifecycle change)</option>
            </select>

            <div style={{ display: "grid", gap: 4, minWidth: 200 }}>
              <label className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {moveType === "demo"             ? "Demo Pickup Date"
                 : moveType === "sna_to_customer" ? "Scheduled Departure from SNA"
                 :                                  "Scheduled Pickup Date"}
              </label>
              <input
                className="input" type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div style={{ display: "grid", gap: 4, minWidth: 200 }}>
              <label className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {moveType === "demo"             ? "Expected Return to SNA"
                 : moveType === "sna_to_customer" ? "Expected Delivery to Customer"
                 :                                  "Expected Arrival at SNA"}{" "}
                <span style={{ fontWeight: 400, textTransform: "none" }}>(optional)</span>
              </label>
              <input
                className="input" type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div style={{ display: "flex", gap: 6, alignItems: "center", flex: "1 1 260px", minWidth: 220 }}>
              <select
                className="input"
                value={destinationId}
                onChange={(e) => {
                  const v = e.target.value;
                  setDestinationId(v);
                  // Populate the free-text field with the destination's name
                  // so legacy views still show something sensible. Server also
                  // overrides from destination_id on save.
                  if (v) {
                    const picked = destinations.find(d => String(d.id) === String(v));
                    if (picked) setDestination(picked.name);
                  }
                }}
                style={{ flex: 1 }}
                title="Pick from destinations (admin → Destinations)"
              >
                <option value="">— Destination (pick from list) —</option>
                {destinations.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name}{d.city ? ` · ${d.city}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <input
              className="input"
              placeholder={destinationId ? "Override name (optional)" : "Or type a destination"}
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
            />
            <input className="input" placeholder="Airline" value={airline} onChange={(e) => setAirline(e.target.value)} />
            <input className="input" placeholder="Flight Num" value={flightNum} onChange={(e) => setFlightNum(e.target.value)} />
            <input className="input" placeholder="Confirmation" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
            <input className="input" placeholder="Permits" value={permits} onChange={(e) => setPermits(e.target.value)} />

            <select className="input" value={driverId} onChange={(e) => setDriverId(e.target.value)}>
              <option value="">Select Driver</option>
              {(() => {
                // Sort: drivers whose home_terminal matches a word in the
                // destination float to the top. This is soft — user can still
                // pick anyone.
                const dest = String(destination || "").toLowerCase();
                const match = (d) => {
                  const t = String(d.home_terminal || "").toLowerCase();
                  return !!t && dest && dest.includes(t);
                };
                const sorted = [...drivers].sort((a, b) => {
                  const am = match(a), bm = match(b);
                  if (am !== bm) return am ? -1 : 1;
                  return String(a.FullName || "").localeCompare(String(b.FullName || ""));
                });
                return sorted.map((drv) => {
                  const terminal = drv.home_terminal ? ` · ${drv.home_terminal}` : "";
                  const star = match(drv) ? " ★" : "";
                  return (
                    <option key={drv.DriverId} value={drv.DriverId}>
                      {(drv.FullName || `Driver #${drv.DriverId}`) + terminal + star}
                    </option>
                  );
                });
              })()}
            </select>

            <input className="input" placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />

            <div className="row" style={{ gap: 10, alignItems: "center" }}>
              <input className="input" placeholder="Inventory ID" value={inventoryId} readOnly />
              <button className="btn-outline" onClick={openSearchModal} disabled={busy}>
                Search Inventory
              </button>
            </div>

            <input className="input" placeholder="RO WO" value={rowo} onChange={(e) => setRowo(e.target.value)} />
            <input className="input" placeholder="Chassis VIN" value={chassisVin} onChange={(e) => setChassisVin(e.target.value)} />

            <button className="btn" onClick={createOrUpdateMove} disabled={busy}>
              {editMode ? "Update" : "Create"}
            </button>
          </div>

          {editMode ? (
            <div style={{ marginTop: 20 }}>
              <CardComponent
                record={selectedInvRec || (selectedInvId != null ? { id: selectedInvId } : {})}
                table="all_inventory"
              />
              {selectedInvId != null && selectedInvRec === null ? (
                <div className="muted" style={{ marginTop: 8 }}>
                  Couldn’t load inventory record #{selectedInvId}.
                </div>
              ) : null}
            </div>
          ) : null}

          <button className="btn-outline" onClick={closeModal} disabled={busy}>
            Close
          </button>
        </Modal>
      )}

      {breakdownMove && (
        <ReportBreakdownModal
          move={breakdownMove}
          invRec={invById[toNumId(breakdownMove.inventory_id)] || null}
          onClose={() => setBreakdownMove(null)}
          onSaved={async () => {
            setBreakdownMove(null);
            setMsg("Breakdown reported.");
            await Promise.all([loadMoves(), loadOpenBreakdowns()]);
          }}
        />
      )}

      {resolveBreakdown && (
        <ResolveBreakdownModal
          breakdown={resolveBreakdown}
          onClose={() => setResolveBreakdown(null)}
          onSaved={async () => {
            setResolveBreakdown(null);
            setMsg("Breakdown resolved — unit marked ready for pickup from repair.");
            await loadOpenBreakdowns();
          }}
        />
      )}

      {attachmentsMove && (
        <MoveAttachmentsModal
          move={attachmentsMove}
          attachments={attachmentsByMove[attachmentsMove.TripId] || []}
          onClose={() => setAttachmentsMove(null)}
          onChanged={async () => { await loadMoveAttachments(attachmentsMove.TripId); }}
        />
      )}

      {notesBreakdown && (
        <BreakdownNotesModal
          breakdown={notesBreakdown}
          onClose={() => setNotesBreakdown(null)}
        />
      )}

      <NotesWindow
        open={notesOpenId != null}
        inventoryId={notesOpenId ?? 0}
        inventoryTitle={notesTitle}
        onClose={() => setNotesOpenId(null)}
      />

      {showSearchModal && (
        <Modal onClose={() => setShowSearchModal(false)}>
          <h2 style={{ marginTop: 0 }}>Pick a Unit</h2>

          {/* Ready-at-Factory bucket — oldest-first so operator can prioritise
              units that have been sitting longest. Hidden if empty so the
              modal stays focused on search when there's nothing waiting. */}
          <div className="card" style={{ background: "#f0fdf4", border: "1px solid #86efac", marginBottom: 14 }}>
            <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontWeight: 800, color: "#166534" }}>
                🏭 Ready at Factory {readyUnits.length ? `(${readyUnits.length})` : ""}
              </div>
              <button className="btn-outline" onClick={loadReadyUnits} disabled={readyBusy} style={{ fontSize: 12 }}>
                {readyBusy ? "Loading…" : "Refresh"}
              </button>
            </div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              Units flagged ready but not yet picked up, sorted by how long they've been waiting.
            </div>
            {readyBusy && readyUnits.length === 0 ? (
              <div className="muted">Loading ready units…</div>
            ) : readyUnits.length === 0 ? (
              <div className="muted">No units are currently flagged Ready at Factory.</div>
            ) : (
              <div style={{ display: "grid", gap: 8, maxHeight: 260, overflowY: "auto" }}>
                {readyUnits.map((u) => {
                  const age = ageFromTimestamp(u.ready_at_factory);
                  const badge = ageBadge(age?.hours);
                  return (
                    <div
                      key={u.id}
                      className="card"
                      style={{ cursor: "pointer", background: "#fff", borderLeft: `4px solid ${badge.color}` }}
                      onClick={() => selectInventory(u)}
                      title="Use this unit for the new move"
                    >
                      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <div style={{ fontWeight: 700 }}>
                          {u.rowo || `#${u.id}`}
                          {u.stock_po && <span style={{ opacity: 0.6, fontWeight: 500 }}> · {u.stock_po}</span>}
                        </div>
                        <span style={{
                          fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 99,
                          background: badge.color, color: "#fff", whiteSpace: "nowrap",
                        }}>
                          {age?.label || "age unknown"}
                        </span>
                      </div>
                      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                        {u.customer && <>For: <strong>{u.customer}</strong> · </>}
                        {u.body_make_model || u.chassis_make_model || "—"}
                        {u.chassis_vin && <> · VIN {String(u.chassis_vin).slice(-8)}</>}
                      </div>
                      {(u.ready_at_factory || u.unit_location) && (
                        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                          {u.ready_at_factory && <>Ready since {String(u.ready_at_factory).slice(0, 10)}</>}
                          {u.ready_at_factory && u.unit_location && " · "}
                          {u.unit_location && <>📍 {u.unit_location}</>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ fontWeight: 800, marginBottom: 6 }}>Or search by RO / WO / VIN</div>
          <div className="row" style={{ gap: 10 }}>
            <input
              className="input"
              placeholder="Search by RO/WO, Factory WO, Stock PO"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") searchInventory(); }}
            />
            <button className="btn" onClick={searchInventory} disabled={searchBusy}>
              Search
            </button>
          </div>

          {searchBusy ? <div className="muted">Searching...</div> : null}

          <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
            {searchResults.map((result) => (
              <div
                key={result.id}
                className="card"
                style={{ cursor: "pointer", background: "rgba(255,255,255,0.02)" }}
                onClick={() => selectInventory(result)}
              >
                <div>ID: {result.id}</div>
                <div>RO/WO: {result.rowo || "(none)"}</div>
                <div>Factory WO: {result.factory_wo || "(none)"}</div>
                <div>Stock PO: {result.stock_po || "(none)"}</div>
              </div>
            ))}
            {searchResults.length === 0 && !searchBusy && searchQuery ? <div className="muted">No results found.</div> : null}
          </div>

          <div className="row" style={{ justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn-outline" onClick={() => setShowSearchModal(false)}>
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Report Breakdown modal ──────────────────────────────────────────────────
function ReportBreakdownModal({ move, invRec, onClose, onSaved }) {
  const [location, setLocation]     = useState("");
  const [svcdealer, setSvcdealer]   = useState("");
  const [caseVendor, setCaseVendor] = useState("MackOne");
  const [casenumber, setCasenumber] = useState("");
  const [reason, setReason]         = useState("");
  const [adHocInput, setAdHocInput] = useState("");
  const [adHocList, setAdHocList]   = useState([]);
  const [releaseDriver, setReleaseDriver] = useState(false);
  const [busy, setBusy]             = useState(false);
  const [err, setErr]               = useState("");

  const addEmail = () => {
    const v = adHocInput.trim();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { setErr("Invalid email address"); return; }
    if (adHocList.includes(v)) { setAdHocInput(""); return; }
    setAdHocList([...adHocList, v]); setAdHocInput(""); setErr("");
  };

  const removeEmail = (e) => setAdHocList(adHocList.filter(x => x !== e));

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post(`/units/${move.inventory_id}/breakdown`, {
        move_id:            move.TripId,
        driver_id:          move.driver_id || null,
        location:           location.trim(),
        svcdealer:          svcdealer.trim(),
        case_vendor:        caseVendor,
        casenumber:         casenumber.trim(),
        reason:             reason.trim(),
        ad_hoc_recipients:  adHocList,
        release_driver:     releaseDriver,
      });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || "Report failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 style={{ marginTop: 0, color: "#dc2626" }}>🚨 Report Breakdown</h2>
      <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
        Move #{move.TripId}{invRec?.rowo ? ` · Unit ${invRec.rowo}` : ""}{invRec?.stock_po ? ` · Stock/PO ${invRec.stock_po}` : ""}
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        <div>
          <label className="muted" style={{ fontSize: 12 }}>Location (where the truck is)</label>
          <input className="input" style={{ width: "100%" }} value={location} onChange={e => setLocation(e.target.value)}
            placeholder="e.g. I-40 MM 284, Amarillo TX" />
        </div>
        <div>
          <label className="muted" style={{ fontSize: 12 }}>Servicing Dealer</label>
          <input className="input" style={{ width: "100%" }} value={svcdealer} onChange={e => setSvcdealer(e.target.value)}
            placeholder="e.g. Kenworth of Dallas" />
        </div>
        <div className="row" style={{ gap: 10 }}>
          <div style={{ flex: "0 0 160px" }}>
            <label className="muted" style={{ fontSize: 12 }}>Case Vendor</label>
            <select className="input" style={{ width: "100%" }} value={caseVendor} onChange={e => setCaseVendor(e.target.value)}>
              <option>MackOne</option>
              <option>Volvo Action Service</option>
              <option>Peterbilt PACCAR Solutions</option>
              <option>Kenworth TruckTech+</option>
              <option>Other</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label className="muted" style={{ fontSize: 12 }}>Case #</label>
            <input className="input" style={{ width: "100%" }} value={casenumber} onChange={e => setCasenumber(e.target.value)}
              placeholder="Roadside case number" />
          </div>
        </div>
        <div>
          <label className="muted" style={{ fontSize: 12 }}>Complaint / Reason</label>
          <textarea className="input" style={{ width: "100%", minHeight: 80 }} value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Check engine light + loss of power, towed to dealer" />
        </div>
        {move.driver_id && (
          <div style={{
            display: "flex", alignItems: "flex-start", gap: 10, padding: 12,
            border: `1px solid ${releaseDriver ? "#dc2626" : "var(--card-border)"}`,
            borderRadius: 8,
            background: releaseDriver ? "rgba(220,38,38,0.08)" : "transparent",
          }}>
            <input
              type="checkbox"
              id="release_driver_cb"
              checked={releaseDriver}
              onChange={e => setReleaseDriver(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <label htmlFor="release_driver_cb" style={{ cursor: "pointer", flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>Release driver from this move</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 3, lineHeight: 1.4 }}>
                Leave <strong>unchecked</strong> when the driver will wait (hotel) while the unit is repaired —
                they stay assigned and the move stays open. Check only when the repair will exceed tolerable wait time
                and the driver should go home. A new driver (or the same one) can be assigned later to pick up from repair.
              </div>
            </label>
          </div>
        )}

        <div>
          <label className="muted" style={{ fontSize: 12 }}>
            Ad-hoc notification emails <span style={{ fontWeight: 400 }}>(in addition to the default recipients for this event)</span>
          </label>
          <div className="row" style={{ gap: 6 }}>
            <input className="input" style={{ flex: 1 }} value={adHocInput}
              onChange={e => setAdHocInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
              placeholder="someone@example.com and press Add" />
            <button className="btn-outline" onClick={addEmail}>Add</button>
          </div>
          {adHocList.length > 0 && (
            <div className="row" style={{ gap: 6, flexWrap: "wrap", marginTop: 8 }}>
              {adHocList.map(e => (
                <span key={e} style={{ background: "#fef2f2", color: "#991b1b", borderRadius: 99, padding: "4px 10px", fontSize: 12, fontWeight: 600 }}>
                  {e}
                  <button onClick={() => removeEmail(e)} style={{ marginLeft: 6, background: "none", border: "none", color: "#991b1b", cursor: "pointer", fontWeight: 700 }}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {err && <div className="card" style={{ marginTop: 10, color: "#dc2626" }}>{err}</div>}

      <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
        <button className="btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn" style={{ background: "#dc2626" }} onClick={submit} disabled={busy}>
          {busy ? "Reporting…" : "Report Breakdown"}
        </button>
      </div>
      <div className="muted" style={{ fontSize: 11, marginTop: 10 }}>
        Reporting stamps the unit as broken down and sends the breakdown notification to the default recipients
        plus any ad-hoc emails above. The driver stays assigned by default — check <strong>Release driver</strong> above
        only when the driver is going home and not waiting for the repair.
      </div>
    </Modal>
  );
}

// ── Resolve Breakdown modal (mark repaired) ────────────────────────────────
function ResolveBreakdownModal({ breakdown, onClose, onSaved }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState("");

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post(`/breakdowns/${breakdown.id}/resolve`, { resolution_notes: notes.trim() || null });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || "Resolve failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 style={{ marginTop: 0, color: "#059669" }}>✅ Mark Repaired</h2>
      <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
        Unit {breakdown.rowo || `#${breakdown.inventory_id}`} · Case {breakdown.casenumber || "—"} · {breakdown.svcdealer || "no dealer"}
      </div>
      {breakdown.reason && (
        <div className="card" style={{ background: "#fef2f2", marginBottom: 12, fontSize: 13 }}>
          <strong>Original complaint:</strong> {breakdown.reason}
        </div>
      )}

      <label className="muted" style={{ fontSize: 12 }}>Repair notes (optional)</label>
      <textarea className="input" style={{ width: "100%", minHeight: 100 }} value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="e.g. Replaced turbo, road-tested, cleared codes" />

      {err && <div className="card" style={{ marginTop: 10, color: "#dc2626" }}>{err}</div>}

      <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
        <button className="btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn" style={{ background: "#059669" }} onClick={submit} disabled={busy}>
          {busy ? "Saving…" : "Mark Repaired"}
        </button>
      </div>
      <div className="muted" style={{ fontSize: 11, marginTop: 10 }}>
        Marks the unit ready for pickup from repair and sends the <code>unit_repaired</code> notification
        to the default recipients plus anyone who was ad-hoc notified when the breakdown was first reported.
        Assign a driver to the move when you're ready to pick up from the dealer.
      </div>
    </Modal>
  );
}

// ── Move Attachments modal ─────────────────────────────────────────────────
// Upload / list / delete airline tickets, permits, and any other docs for a move.
// Uploaded files become available to the assigned driver via the portal.
const ATTACH_KIND_LABELS = {
  airline_ticket: "Airline Ticket",
  permit:         "Permit",
  other:          "Other",
};
function fmtBytes(n) {
  if (!Number.isFinite(+n) || +n <= 0) return "";
  const kb = +n / 1024; if (kb < 1024) return `${kb.toFixed(0)} KB`;
  const mb = kb / 1024;  return `${mb.toFixed(1)} MB`;
}
function MoveAttachmentsModal({ move, attachments, onClose, onChanged }) {
  const [kind, setKind]     = useState("airline_ticket");
  const [notes, setNotes]   = useState("");
  const [file, setFile]     = useState(null);
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState("");

  const submit = async () => {
    if (!file) { setErr("Pick a file to upload."); return; }
    setErr(""); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      if (notes.trim()) fd.append("notes", notes.trim());
      await api.post(`/moves/${move.TripId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile(null); setNotes("");
      await onChanged();
    } catch (e) {
      setErr(e?.response?.data?.error || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this attachment? It will no longer be visible to the driver.")) return;
    try {
      await api.delete(`/moves/${move.TripId}/attachments/${id}`);
      await onChanged();
    } catch (e) {
      setErr(e?.response?.data?.error || "Delete failed");
    }
  };

  const changeKind = async (att, newKind) => {
    try {
      await api.patch(`/moves/${move.TripId}/attachments/${att.id}`, { kind: newKind });
      await onChanged();
    } catch (e) {
      setErr(e?.response?.data?.error || "Update failed");
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 style={{ marginTop: 0 }}>📎 Move Documents</h2>
      <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
        Trip #{move.TripId}{move.destination ? ` · ${move.destination}` : ""} — uploaded files are available to the
        assigned driver in the portal.
      </div>

      {/* Upload form */}
      <div className="card" style={{ background: "#f8fafc", marginBottom: 14 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Upload new document</div>
        <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <select className="input" value={kind} onChange={(e) => setKind(e.target.value)} style={{ maxWidth: 200 }}>
            <option value="airline_ticket">Airline Ticket</option>
            <option value="permit">Permit</option>
            <option value="other">Other</option>
          </select>
          <input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.heif,.doc,.docx,.xls,.xlsx,.txt"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            style={{ flex: "1 1 240px" }}
          />
        </div>
        <input
          className="input" style={{ width: "100%", marginTop: 8 }}
          placeholder="Notes (optional — e.g. 'Seat 14A, gate 42', 'Alberta oversize permit #PN-1234')"
          value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500}
        />
        {err && <div style={{ marginTop: 8, color: "#dc2626", fontSize: 13 }}>{err}</div>}
        <div className="row" style={{ justifyContent: "flex-end", marginTop: 10 }}>
          <button className="btn" onClick={submit} disabled={busy || !file}>
            {busy ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>

      {/* List */}
      <div style={{ fontWeight: 700, marginBottom: 6 }}>Attached documents ({attachments.length})</div>
      {attachments.length === 0 ? (
        <div className="muted" style={{ fontSize: 13 }}>No attachments yet.</div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {attachments.map((a) => (
            <div key={a.id} className="card" style={{ background: "#fff", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontWeight: 700 }}>
                  <a href={a.url} target="_blank" rel="noreferrer" style={{ color: "#1d4ed8" }}>
                    {a.original_name}
                  </a>
                </div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {ATTACH_KIND_LABELS[a.kind] || a.kind}
                  {a.size_bytes ? ` · ${fmtBytes(a.size_bytes)}` : ""}
                  {a.uploaded_at ? ` · ${String(a.uploaded_at).slice(0, 16).replace("T", " ")}` : ""}
                </div>
                {a.notes && <div style={{ fontSize: 12, marginTop: 2 }}>{a.notes}</div>}
              </div>
              <select
                className="input" value={a.kind} onChange={(e) => changeKind(a, e.target.value)}
                style={{ maxWidth: 170 }}
              >
                <option value="airline_ticket">Airline Ticket</option>
                <option value="permit">Permit</option>
                <option value="other">Other</option>
              </select>
              <button
                className="btn-outline"
                style={{ color: "#dc2626", borderColor: "#dc2626" }}
                onClick={() => remove(a.id)}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="row" style={{ justifyContent: "flex-end", marginTop: 14 }}>
        <button className="btn-outline" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}

// ── Breakdown notes modal (admin side) ────────────────────────────────────
// Shared thread with the assigned driver — they see the same notes on the
// portal. Admin notes show as "Dispatch", driver notes as "Driver".
function BreakdownNotesModal({ breakdown, onClose }) {
  const [notes, setNotes]   = useState([]);
  const [draft, setDraft]   = useState("");
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState("");

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get(`/breakdowns/${breakdown.id}/notes`);
      setNotes(Array.isArray(data?.notes) ? data.notes : []);
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to load notes.");
    } finally { setBusy(false); }
  }, [breakdown.id]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const text = draft.trim();
    if (!text) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/breakdowns/${breakdown.id}/notes`, { note_text: text });
      setDraft("");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.error || "Note failed.");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose}>
      <h2 style={{ marginTop: 0, color: "#991b1b" }}>💬 Breakdown Notes</h2>
      <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
        Case #{breakdown.casenumber || "—"} · {breakdown.rowo || `Unit #${breakdown.inventory_id}`}
        {breakdown.location && ` · 📍 ${breakdown.location}`}
        <br />Notes are shared with the assigned driver in real time.
      </div>

      <div style={{ display: "grid", gap: 8, maxHeight: 360, overflowY: "auto", padding: 4 }}>
        {busy && notes.length === 0 ? <div className="muted">Loading…</div> : null}
        {!busy && notes.length === 0 ? <div className="muted">No notes yet.</div> : null}
        {notes.map((n) => {
          const isDriver = n.author_type === "driver";
          return (
            <div key={n.id} style={{
              background: isDriver ? "#eff6ff" : "#fffbeb",
              border: `1px solid ${isDriver ? "#bfdbfe" : "#fde68a"}`,
              borderRadius: 10, padding: "10px 12px",
            }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase",
                color: isDriver ? "#1d4ed8" : "#b45309" }}>
                {isDriver ? "👤 Driver" : "🧑‍💼 Dispatch"}
                {n.author_name && ` · ${n.author_name}`}
                {n.created_at && ` · ${String(n.created_at).slice(0, 16).replace("T", " ")}`}
              </div>
              <div style={{ marginTop: 4, whiteSpace: "pre-wrap", fontSize: 14 }}>{n.note_text}</div>
            </div>
          );
        })}
      </div>

      <textarea
        className="input"
        placeholder="Add a note — dealer update, repair status, driver comms…"
        value={draft} onChange={(e) => setDraft(e.target.value)}
        style={{ width: "100%", minHeight: 80, marginTop: 12, boxSizing: "border-box" }}
      />
      {err && <div style={{ color: "#dc2626", marginTop: 6, fontSize: 13 }}>{err}</div>}
      <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
        <button className="btn-outline" onClick={onClose}>Close</button>
        <button className="btn" onClick={submit} disabled={busy || !draft.trim()}>
          {busy ? "Saving…" : "Post Note"}
        </button>
      </div>
    </Modal>
  );
}
